import {
  DragEvent as ReactDragEvent,
  FormEvent,
  PointerEvent as ReactPointerEvent,
  ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ApigenConnectionError, fetchCalls, fileToBase64, recoverDroppedCall, runEndpoint } from '../api';
import { recomputeCost, usePricingVersion } from '../pricingOverrides';
import { DEFAULT_GENERATION_TIER, GENERATION_TIERS } from '../types';
import type { ApiCallResponse, ApiEndpointModel, ApiInputSpec, GenerationTier } from '../types';
import { BeforeAfterSlider } from './BeforeAfterSlider';
import { EndpointBadge } from './EndpointBadge';
import { ImageLightbox } from './ImageLightbox';

interface FileValue {
  file: File | null;
  previewUrl: string | null;
  url: string;
}

/** One image in a multi-image (`images`) input: a picked file or a stored URL. */
interface MultiImageEntry {
  file: File | null;
  previewUrl: string | null;
  url: string;
}

const emptyFileValue: FileValue = { file: null, previewUrl: null, url: '' };

const VIEW_OPTIONS = ['left', 'right', 'front', 'rear'];

function randomUuid(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

// Firestore-style 20-char id, e.g. "HDaSz4ke4clyq6dg1maA".
function randomFirestoreId(): string {
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let out = '';
  for (let i = 0; i < 20; i += 1) {
    out += alphabet[Math.floor(Math.random() * alphabet.length)];
  }
  return out;
}

function randomEmail(): string {
  const names = ['alex', 'sam', 'jordan', 'casey', 'riley', 'taylor', 'morgan', 'jamie'];
  const name = names[Math.floor(Math.random() * names.length)];
  return `${name}${Math.floor(Math.random() * 1000)}@uveye.com`;
}

function randomDvp(): string {
  return `DVP-${Math.floor(1000 + Math.random() * 9000)}`;
}

export interface PlaygroundPrefill {
  /** Endpoint key the request was run with; prefill only applies to it. */
  modelKey: string;
  prompt?: string | null;
  params?: Record<string, unknown> | null;
  seed?: number | null;
  /** Metadata envelope to restore (scan/org/job/view/player/dvp). */
  meta?: {
    scan_id?: string | null;
    organization_id?: string | null;
    job_id?: string | null;
    view?: string | null;
    player?: string | null;
    dvp?: string | null;
  };
  /** Stored input image/mask URLs, keyed by input name. */
  inputRefs?: Record<string, string> | null;
  /** Previous result to show in the Result panel. */
  result?: ApiCallResponse | null;
}

interface PlaygroundTabProps {
  model: ApiEndpointModel;
  /** Optional values to seed the form with (from "re-run in Playground"). */
  prefill?: PlaygroundPrefill;
}

export function PlaygroundTab({ model, prefill }: PlaygroundTabProps) {
  // Metadata envelope
  const [scanId, setScanId] = useState('');
  const [organizationId, setOrganizationId] = useState('');
  const [jobId, setJobId] = useState('');
  const [view, setView] = useState('');
  const [player, setPlayer] = useState('');
  const [dvp, setDvp] = useState('');
  // The tier a merch run would have stamped on the request; picks which of the
  // endpoint's four setups serves it.
  const [generationTier, setGenerationTier] = useState<GenerationTier>(
    model.default_tier || DEFAULT_GENERATION_TIER,
  );

  // Dynamic input values, keyed by input name.
  const [fileValues, setFileValues] = useState<Record<string, FileValue>>({});
  const [multiImages, setMultiImages] = useState<Record<string, MultiImageEntry[]>>({});
  const [textValues, setTextValues] = useState<Record<string, string>>({});

  const [submitting, setSubmitting] = useState(false);
  const [recovering, setRecovering] = useState(false);
  const [result, setResult] = useState<ApiCallResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lightbox, setLightbox] = useState<string | null>(null);
  const [compare, setCompare] = useState(true);

  // Re-render when a pricing override changes so the displayed cost updates.
  const pricingVersion = usePricingVersion();

  function applyResult(resp: ApiCallResponse) {
    setResult(resp);
    setError(resp.status !== 'success' ? resp.error || 'The call failed' : null);
  }

  function randomizeMeta() {
    setScanId(randomUuid());
    setOrganizationId(randomFirestoreId());
    setJobId(randomUuid());
    setView(VIEW_OPTIONS[Math.floor(Math.random() * VIEW_OPTIONS.length)]);
    setPlayer(randomEmail());
    setDvp(randomDvp());
  }

  // Restore a prior request when opened via "Playground": text inputs,
  // metadata, stored input images, and the previous result.
  useEffect(() => {
    // Only apply a prefill that belongs to the model currently shown.
    if (!prefill || prefill.modelKey !== model.key) return;

    setTextValues((prev) => {
      const next = { ...prev };
      const names = new Set(model.inputs.map((i) => i.name));
      if (prefill.prompt != null && names.has('prompt')) next.prompt = prefill.prompt;
      if (prefill.seed != null && names.has('seed')) next.seed = String(prefill.seed);
      if (prefill.params) {
        for (const [k, v] of Object.entries(prefill.params)) {
          if (names.has(k) && v != null) next[k] = String(v);
        }
      }
      return next;
    });

    if (prefill.meta) {
      const m = prefill.meta;
      if (m.scan_id != null) setScanId(m.scan_id);
      if (m.organization_id != null) setOrganizationId(m.organization_id);
      if (m.job_id != null) setJobId(m.job_id);
      if (m.view != null) setView(m.view);
      if (m.player != null) setPlayer(m.player);
      if (m.dvp != null) setDvp(m.dvp);
    }

    // Restore image/mask inputs from their stored URLs (the backend accepts a
    // URL in place of base64, so a re-run uses the exact same inputs).
    if (prefill.inputRefs) {
      const inputRefs = prefill.inputRefs;
      const typeByName = new Map(model.inputs.map((i) => [i.name, i.type]));

      setFileValues((prev) => {
        const next = { ...prev };
        for (const [name, url] of Object.entries(inputRefs)) {
          const t = typeByName.get(name);
          if (t === 'image' || t === 'mask') {
            const previous = next[name];
            if (previous?.previewUrl) URL.revokeObjectURL(previous.previewUrl);
            next[name] = { file: null, previewUrl: null, url };
          }
        }
        return next;
      });

      // Multi-image inputs are stored as `name`, or `name_0`, `name_1`, … —
      // except the endpoints that persist extra images under the generic
      // `reference_0`, `reference_1`, … convention (nano-banana reference
      // images, wheel-replacement extra wheels), which is what the async
      // worker and `buildReproBody` read back.
      setMultiImages((prev) => {
        const next = { ...prev };
        for (const spec of model.inputs) {
          if (spec.type !== 'images') continue;
          const suffixRe = new RegExp(`^${spec.name}_(\\d+)$`);
          const found: { idx: number; url: string }[] = [];
          for (const [name, url] of Object.entries(inputRefs)) {
            if (name === spec.name) found.push({ idx: 0, url });
            else {
              const m = name.match(suffixRe);
              if (m) found.push({ idx: Number(m[1]), url });
            }
          }
          if (!found.length) {
            for (const [name, url] of Object.entries(inputRefs)) {
              const m = name.match(/^reference_(\d+)$/);
              if (m) found.push({ idx: Number(m[1]), url });
            }
          }
          if (found.length) {
            found.sort((a, b) => a.idx - b.idx);
            next[spec.name] = found.map((f) => ({ file: null, previewUrl: null, url: f.url }));
          }
        }
        return next;
      });
    }

    if (prefill.result !== undefined) {
      setResult(prefill.result);
      setError(
        prefill.result && prefill.result.status !== 'success'
          ? prefill.result.error || 'The call failed'
          : null,
      );
    }
  }, [prefill, model]);

  function setFile(name: string, file: File | null) {
    setFileValues((prev) => {
      const previous = prev[name];
      if (previous?.previewUrl) URL.revokeObjectURL(previous.previewUrl);
      return {
        ...prev,
        [name]: {
          file,
          previewUrl: file ? URL.createObjectURL(file) : null,
          url: previous?.url || '',
        },
      };
    });
  }

  function setFileUrl(name: string, url: string) {
    setFileValues((prev) => ({
      ...prev,
      [name]: { ...(prev[name] || emptyFileValue), url },
    }));
  }

  async function buildBody(m: ApiEndpointModel): Promise<Record<string, unknown>> {
    const body: Record<string, unknown> = {};
    if (scanId.trim()) body.scan_id = scanId.trim();
    if (organizationId.trim()) body.organization_id = organizationId.trim();
    if (jobId.trim()) body.job_id = jobId.trim();
    if (view.trim()) body.view = view.trim();
    if (player.trim()) body.player = player.trim();
    if (dvp.trim()) body.dvp = dvp.trim();
    if (m.tiered) body.generation_tier = generationTier;

    for (const spec of m.inputs) {
      if (spec.type === 'image' || spec.type === 'mask') {
        const fv = fileValues[spec.name];
        if (fv?.file) {
          body[spec.name] = await fileToBase64(fv.file);
        } else if (fv?.url?.trim()) {
          body[spec.name] = fv.url.trim();
        } else if (spec.required) {
          throw new Error(`${spec.label} is required`);
        }
      } else if (spec.type === 'images') {
        const entries = multiImages[spec.name] || [];
        const vals: string[] = [];
        for (const entry of entries) {
          if (entry.file) vals.push(await fileToBase64(entry.file));
          else if (entry.url.trim()) vals.push(entry.url.trim());
        }
        if (vals.length > 0) body[spec.name] = vals;
        else if (spec.required) throw new Error(`${spec.label} is required`);
      } else if (spec.type === 'number') {
        const v = textValues[spec.name];
        if (v != null && v !== '') body[spec.name] = Number(v);
        else if (spec.required) throw new Error(`${spec.label} is required`);
      } else if (spec.type === 'select') {
        const v = (textValues[spec.name] || spec.default || '').trim();
        if (v) body[spec.name] = v;
        else if (spec.required) throw new Error(`${spec.label} is required`);
      } else {
        const v = (textValues[spec.name] || '').trim();
        if (v) body[spec.name] = v;
        else if (spec.required) throw new Error(`${spec.label} is required`);
      }
    }
    return body;
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setRecovering(false);
    setError(null);
    setResult(null);
    try {
      const body = await buildBody(model);

      // Snapshot the current history so a dropped connection can be recovered:
      // the completed run appears as a *new* record id absent from this set.
      let knownIds = new Set<string>();
      try {
        const existing = await fetchCalls(model.key, 20);
        knownIds = new Set(existing.map((c) => c.id));
      } catch {
        // A failed snapshot only weakens recovery; proceed with the run anyway.
      }

      try {
        applyResult(await runEndpoint(model.path, body));
      } catch (ex) {
        // The connection dropped, but the generation may have finished
        // server-side. Poll the history for the fresh record before surfacing
        // the raw network error.
        if (ex instanceof ApigenConnectionError) {
          setRecovering(true);
          const recovered = await recoverDroppedCall(model.key, knownIds).finally(() =>
            setRecovering(false),
          );
          if (recovered) {
            applyResult(recovered);
          } else {
            setError(ex.message);
          }
        } else {
          throw ex;
        }
      }
    } catch (ex) {
      setError(ex instanceof Error ? ex.message : String(ex));
    } finally {
      setSubmitting(false);
    }
  }

  // The first input image: used both as the mask-drawing base and as the
  // "before" image in the result comparison. Handles both single `image` inputs
  // and multi-image (`images`) inputs (first entry).
  const primaryImageSpec = model.inputs.find((s) => s.type === 'image' || s.type === 'images');
  let sourceImageSrc = '';
  if (primaryImageSpec) {
    if (primaryImageSpec.type === 'image') {
      const v = fileValues[primaryImageSpec.name];
      sourceImageSrc = v?.previewUrl || v?.url.trim() || '';
    } else {
      const first = (multiImages[primaryImageSpec.name] || [])[0];
      sourceImageSrc = first?.previewUrl || first?.url.trim() || '';
    }
  }

  const outputSrc = result?.output_image
    ? `data:image/png;base64,${result.output_image}`
    : result?.output_url || null;
  const canCompare = Boolean(sourceImageSrc && outputSrc);

  const effectiveCost = useMemo(() => {
    void pricingVersion; // recompute when the pricing override changes
    return recomputeCost(model, result?.cost_usd ?? null);
  }, [model, result, pricingVersion]);

  return (
    <>
    <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-2 gap-5">
      {/* Left column: inputs */}
      <div className="space-y-5">
        <div className="bg-white rounded-xl border border-slate-200 shadow-soft p-5">
          <h2 className="text-sm font-semibold text-slate-700 mb-3">Inputs</h2>
          <div className="space-y-4">
            {model.inputs.map((spec) => (
              <InputField
                key={spec.name}
                spec={spec}
                fileValue={fileValues[spec.name] || emptyFileValue}
                multiImages={multiImages[spec.name] || []}
                textValue={textValues[spec.name] || ''}
                sourceImageSrc={sourceImageSrc}
                onFile={(f) => setFile(spec.name, f)}
                onFileUrl={(u) => setFileUrl(spec.name, u)}
                onMultiImages={(entries) => setMultiImages((p) => ({ ...p, [spec.name]: entries }))}
                onText={(v) => setTextValues((p) => ({ ...p, [spec.name]: v }))}
              />
            ))}
          </div>
        </div>

        <button
          type="submit"
          disabled={submitting}
          className="w-full inline-flex items-center justify-center gap-2 px-4 h-11 rounded-lg bg-gradient-to-r from-indigo-500 to-purple-500 text-white font-medium text-sm shadow-md hover:shadow-lg hover:opacity-95 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {submitting ? 'Running…' : 'Run endpoint'}
        </button>
      </div>

      {/* Right column: result, then request metadata */}
      <div className="space-y-5">
        <div className="bg-white rounded-xl border border-slate-200 shadow-soft p-5 min-h-[12rem]">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2 min-w-0">
              <h2 className="text-sm font-semibold text-slate-700 shrink-0">Result</h2>
              {result && <EndpointBadge provider={result.provider} modelId={result.model_id} />}
            </div>
            {canCompare && (
              <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden">
                <button
                  type="button"
                  onClick={() => setCompare(false)}
                  className={`px-2.5 py-1 text-xs font-medium transition-colors ${
                    !compare ? 'bg-indigo-50 text-indigo-600' : 'bg-white text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  Result
                </button>
                <button
                  type="button"
                  onClick={() => setCompare(true)}
                  className={`px-2.5 py-1 text-xs font-medium transition-colors ${
                    compare ? 'bg-indigo-50 text-indigo-600' : 'bg-white text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  Compare
                </button>
              </div>
            )}
          </div>

          {error && (
            <div className="rounded-lg border border-danger/30 bg-danger/5 text-danger px-3 py-2 text-sm mb-3">
              {error}
            </div>
          )}

          {!result && !error && !submitting && (
            <p className="text-sm text-slate-400">Run the endpoint to see the output here.</p>
          )}
          {submitting && (
            <div className="flex flex-col items-center gap-3 py-8">
              <div
                role="status"
                aria-label="Loading"
                className="h-10 w-10 rounded-full border-4 border-slate-200 border-t-indigo-500 animate-spin"
              />
              <p className="text-sm text-slate-500">
                {recovering
                  ? 'Connection dropped — recovering the result from history…'
                  : 'Waiting for the provider…'}
              </p>
            </div>
          )}

          {outputSrc &&
            (canCompare && compare ? (
              <BeforeAfterSlider before={sourceImageSrc} after={outputSrc} />
            ) : (
              <button
                type="button"
                onClick={() => setLightbox(outputSrc)}
                className="block w-full"
                title="Click to expand"
              >
                <img
                  src={outputSrc}
                  alt="Output"
                  className="w-full rounded-lg border border-slate-200 bg-surface-50 cursor-zoom-in"
                />
              </button>
            ))}

          {result && (
            <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
              <ResultRow label="Status" value={result.status} />
              <ResultRow label="Provider" value={result.provider || '—'} />
              <ResultRow label="Model" value={result.model_id || '—'} mono />
              <ResultRow
                label="Cost"
                value={
                  effectiveCost.cost != null
                    ? `$${effectiveCost.cost.toFixed(5)}${effectiveCost.estimated ? ' (est.)' : ''}`
                    : '—'
                }
                title={
                  effectiveCost.estimated
                    ? 'Estimated from your Settings pricing. Token counts are not stored, so this is a proportional estimate, not the recorded cost.'
                    : undefined
                }
              />
              <ResultRow
                label="Elapsed"
                value={result.elapsed_ms != null ? `${result.elapsed_ms} ms` : '—'}
              />
              <ResultRow label="Record ID" value={result.record_id} mono />
              {result.output_url && (
                <div className="col-span-2">
                  <a
                    href={result.output_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-indigo-600 hover:underline break-all"
                  >
                    Open stored output (S3)
                  </a>
                </div>
              )}
            </dl>
          )}
        </div>

        <div className="bg-white rounded-xl border border-slate-200 shadow-soft p-5">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-slate-700">Request metadata</h2>
            <button
              type="button"
              onClick={randomizeMeta}
              className="px-3 py-1.5 text-xs font-medium text-indigo-700 bg-indigo-50 hover:bg-indigo-100 rounded-md"
            >
              Randomize
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <LabeledInput label="Scan ID" value={scanId} onChange={setScanId} placeholder="inspection id" />
            <LabeledInput
              label="Organization ID"
              value={organizationId}
              onChange={setOrganizationId}
              placeholder="org id"
            />
            <LabeledInput label="Job ID" value={jobId} onChange={setJobId} placeholder="job id" />
            <LabeledInput label="View" value={view} onChange={setView} placeholder="left / rear / …" />
            <LabeledInput
              label="Player"
              value={player}
              onChange={setPlayer}
              placeholder="player@uveye.com"
              type="email"
            />
            <LabeledInput label="DVP" value={dvp} onChange={setDvp} placeholder="dvp id" />
            {model.tiered && (
              <label className="block">
                <span className="text-xs font-medium text-slate-500">Generation tier</span>
                <select
                  value={generationTier}
                  onChange={(e) => setGenerationTier(e.target.value as GenerationTier)}
                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400"
                >
                  {GENERATION_TIERS.map((tier) => (
                    <option key={tier} value={tier}>
                      {tier}
                      {tier === (model.default_tier || DEFAULT_GENERATION_TIER) ? ' (default)' : ''}
                    </option>
                  ))}
                </select>
                <span className="mt-1 block text-[11px] text-slate-400">
                  Runs with this tier's setup from the Settings tab.
                </span>
              </label>
            )}
          </div>
        </div>
      </div>
    </form>
    {lightbox && <ImageLightbox src={lightbox} alt="Output" onClose={() => setLightbox(null)} />}
    </>
  );
}

function LabeledInput({
  label,
  value,
  onChange,
  placeholder,
  type = 'text',
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
}) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-slate-500">{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400"
      />
    </label>
  );
}

function ResultRow({
  label,
  value,
  mono,
  title,
}: {
  label: string;
  value: string;
  mono?: boolean;
  title?: string;
}) {
  return (
    <>
      <dt className="text-slate-400">{label}</dt>
      <dd className={`text-slate-700 break-all ${mono ? 'font-mono' : ''}`} title={title}>
        {value}
      </dd>
    </>
  );
}

/**
 * Drag-and-drop wrapper: highlights on drag-over and forwards dropped image
 * files. Wraps the native file picker so both interaction styles work.
 */
function DropZone({
  onFiles,
  multiple,
  className,
  children,
}: {
  onFiles: (files: File[]) => void;
  multiple?: boolean;
  className?: string;
  children: ReactNode;
}) {
  const [active, setActive] = useState(false);

  function onDrop(e: ReactDragEvent<HTMLDivElement>) {
    e.preventDefault();
    setActive(false);
    const files = Array.from(e.dataTransfer.files).filter((f) => f.type.startsWith('image/'));
    if (files.length) onFiles(multiple ? files : [files[0]]);
  }

  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        setActive(true);
      }}
      onDragLeave={(e) => {
        e.preventDefault();
        setActive(false);
      }}
      onDrop={onDrop}
      className={`${className || ''} transition-colors ${
        active ? 'border-indigo-400 bg-indigo-50/60 ring-2 ring-indigo-200' : ''
      }`}
    >
      {children}
    </div>
  );
}

interface InputFieldProps {
  spec: ApiInputSpec;
  fileValue: FileValue;
  multiImages: MultiImageEntry[];
  textValue: string;
  sourceImageSrc?: string;
  onFile: (f: File | null) => void;
  onFileUrl: (u: string) => void;
  onMultiImages: (entries: MultiImageEntry[]) => void;
  onText: (v: string) => void;
}

function InputField({
  spec,
  fileValue,
  multiImages,
  textValue,
  sourceImageSrc,
  onFile,
  onFileUrl,
  onMultiImages,
  onText,
}: InputFieldProps) {
  const [drawing, setDrawing] = useState(false);
  const labelEl = (
    <span className="text-xs font-medium text-slate-500">
      {spec.label}
      {spec.required && <span className="text-danger"> *</span>}
    </span>
  );

  if (spec.type === 'image' || spec.type === 'mask') {
    const isMask = spec.type === 'mask';
    const preview = fileValue.previewUrl || (fileValue.url.trim() ? fileValue.url.trim() : null);
    return (
      <div>
        {labelEl}
        <div className="mt-1 flex items-start gap-3">
          <div className="flex-1 space-y-2">
            <DropZone
              onFiles={(files) => onFile(files[0] ?? null)}
              className="rounded-lg border border-dashed border-slate-300 px-3 py-2"
            >
              <input
                type="file"
                accept="image/*"
                onChange={(e) => onFile(e.target.files?.[0] ?? null)}
                className="block w-full text-xs text-slate-600 file:mr-3 file:rounded-md file:border-0 file:bg-indigo-50 file:px-3 file:py-1.5 file:text-indigo-600 file:text-xs file:font-medium hover:file:bg-indigo-100"
              />
              <p className="mt-1 text-[11px] text-slate-400">Drag &amp; drop an image here, or browse.</p>
            </DropZone>
            <input
              type="text"
              value={fileValue.url}
              onChange={(e) => onFileUrl(e.target.value)}
              placeholder="…or paste an image URL"
              className="w-full rounded-lg border border-slate-200 px-3 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-400"
            />
            {isMask && (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setDrawing(true)}
                  disabled={!sourceImageSrc}
                  title={sourceImageSrc ? 'Draw the mask over the loaded image' : 'Load an image first'}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium text-indigo-700 bg-indigo-50 hover:bg-indigo-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                  </svg>
                  {fileValue.file ? 'Edit mask' : 'Draw mask'}
                </button>
                {!sourceImageSrc && (
                  <span className="text-[11px] text-slate-400">load an image to draw</span>
                )}
              </div>
            )}
          </div>
          {preview && (
            <img
              src={preview}
              alt={spec.label}
              className="h-16 w-16 rounded-md border border-slate-200 object-cover shrink-0 bg-slate-900"
            />
          )}
        </div>
        {isMask && drawing && sourceImageSrc && (
          <MaskDrawModal
            imageSrc={sourceImageSrc}
            onClose={() => setDrawing(false)}
            onSave={(file) => {
              onFile(file);
              setDrawing(false);
            }}
          />
        )}
      </div>
    );
  }

  if (spec.type === 'images') {
    const addFiles = (files: File[]) => {
      const added = files.map((f) => ({
        file: f,
        previewUrl: URL.createObjectURL(f),
        url: '',
      }));
      onMultiImages([...multiImages, ...added]);
    };
    const removeAt = (idx: number) => {
      const entry = multiImages[idx];
      if (entry?.previewUrl) URL.revokeObjectURL(entry.previewUrl);
      onMultiImages(multiImages.filter((_, i) => i !== idx));
    };
    return (
      <div>
        {labelEl}
        <DropZone
          onFiles={addFiles}
          multiple
          className="mt-1 rounded-lg border border-dashed border-slate-300 px-3 py-2"
        >
          <input
            type="file"
            accept="image/*"
            multiple
            onChange={(e) => {
              addFiles(Array.from(e.target.files ?? []));
              e.target.value = '';
            }}
            className="block w-full text-xs text-slate-600 file:mr-3 file:rounded-md file:border-0 file:bg-indigo-50 file:px-3 file:py-1.5 file:text-indigo-600 file:text-xs file:font-medium hover:file:bg-indigo-100"
          />
          <p className="mt-1 text-[11px] text-slate-400">
            Drag &amp; drop images here, or browse. Any mask is drawn on the first image.
          </p>
        </DropZone>
        {multiImages.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-2">
            {multiImages.map((entry, idx) => {
              const src = entry.previewUrl || (entry.url.trim() ? entry.url.trim() : '');
              return (
                <div key={idx} className="relative">
                  {src && (
                    <img
                      src={src}
                      alt={`${spec.label} ${idx + 1}`}
                      className="h-16 w-16 rounded-md border border-slate-200 object-cover bg-slate-900"
                    />
                  )}
                  {idx === 0 && (
                    <span className="absolute bottom-0 left-0 rounded-tr rounded-bl-md bg-indigo-600 px-1 text-[9px] font-semibold text-white">
                      1st
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={() => removeAt(idx)}
                    aria-label="Remove image"
                    className="absolute -right-1.5 -top-1.5 h-5 w-5 rounded-full bg-slate-700 text-white text-xs leading-none shadow hover:bg-slate-900"
                  >
                    ×
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  if (spec.type === 'number') {
    return (
      <label className="block">
        {labelEl}
        <input
          type="number"
          value={textValue}
          onChange={(e) => onText(e.target.value)}
          className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400"
        />
      </label>
    );
  }

  if (spec.type === 'select') {
    const options = spec.options || [];
    const current = textValue || spec.default || options[0] || '';
    return (
      <label className="block">
        {labelEl}
        <select
          value={current}
          onChange={(e) => onText(e.target.value)}
          className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400"
        >
          {options.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      </label>
    );
  }

  // text
  const isPrompt = spec.name === 'prompt';
  return (
    <label className="block">
      {labelEl}
      {isPrompt ? (
        <textarea
          value={textValue}
          onChange={(e) => onText(e.target.value)}
          rows={3}
          className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400"
        />
      ) : (
        <input
          type="text"
          value={textValue}
          onChange={(e) => onText(e.target.value)}
          className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400"
        />
      )}
    </label>
  );
}

interface Stroke {
  size: number;
  points: { x: number; y: number }[];
}

/**
 * Paint a white-on-black mask over the loaded source image. The exported mask
 * is rendered from a separate canvas that never draws the source pixels, so a
 * cross-origin (S3) source image cannot taint the export.
 */
function MaskDrawModal({
  imageSrc,
  onSave,
  onClose,
}: {
  imageSrc: string;
  onSave: (file: File) => void;
  onClose: () => void;
}) {
  const overlayRef = useRef<HTMLCanvasElement | null>(null);
  const strokesRef = useRef<Stroke[]>([]);
  const paintingRef = useRef(false);

  const [natural, setNatural] = useState<{ w: number; h: number } | null>(null);
  const [display, setDisplay] = useState<{ w: number; h: number } | null>(null);
  const [brush, setBrush] = useState(28);
  const [hasStrokes, setHasStrokes] = useState(false);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const natW = img.naturalWidth || img.width;
      const natH = img.naturalHeight || img.height;
      const scale = Math.min(560 / natW, 520 / natH, 1);
      setNatural({ w: natW, h: natH });
      setDisplay({ w: Math.max(1, Math.round(natW * scale)), h: Math.max(1, Math.round(natH * scale)) });
    };
    img.onerror = () => setLoadError(true);
    img.src = imageSrc;
  }, [imageSrc]);

  // Close on Escape.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  function redraw() {
    const canvas = overlayRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = 'rgba(239,68,68,0.7)';
    ctx.fillStyle = 'rgba(239,68,68,0.7)';
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    for (const stroke of strokesRef.current) {
      const pts = stroke.points;
      if (pts.length === 1) {
        ctx.beginPath();
        ctx.arc(pts[0].x, pts[0].y, stroke.size / 2, 0, Math.PI * 2);
        ctx.fill();
        continue;
      }
      ctx.lineWidth = stroke.size;
      ctx.beginPath();
      ctx.moveTo(pts[0].x, pts[0].y);
      for (let i = 1; i < pts.length; i += 1) ctx.lineTo(pts[i].x, pts[i].y);
      ctx.stroke();
    }
  }

  function posOf(e: ReactPointerEvent<HTMLCanvasElement>) {
    const rect = e.currentTarget.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  }

  function onDown(e: ReactPointerEvent<HTMLCanvasElement>) {
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    paintingRef.current = true;
    strokesRef.current.push({ size: brush, points: [posOf(e)] });
    setHasStrokes(true);
    redraw();
  }

  function onMove(e: ReactPointerEvent<HTMLCanvasElement>) {
    if (!paintingRef.current) return;
    strokesRef.current[strokesRef.current.length - 1].points.push(posOf(e));
    redraw();
  }

  function onUp() {
    paintingRef.current = false;
  }

  function undo() {
    strokesRef.current.pop();
    setHasStrokes(strokesRef.current.length > 0);
    redraw();
  }

  function clearAll() {
    strokesRef.current = [];
    setHasStrokes(false);
    redraw();
  }

  async function save() {
    if (!natural || !display) return;
    const scale = natural.w / display.w;
    const mask = document.createElement('canvas');
    mask.width = natural.w;
    mask.height = natural.h;
    const ctx = mask.getContext('2d');
    if (!ctx) return;
    ctx.fillStyle = '#000000';
    ctx.fillRect(0, 0, mask.width, mask.height);
    ctx.strokeStyle = '#ffffff';
    ctx.fillStyle = '#ffffff';
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    for (const stroke of strokesRef.current) {
      const pts = stroke.points;
      const size = stroke.size * scale;
      if (pts.length === 1) {
        ctx.beginPath();
        ctx.arc(pts[0].x * scale, pts[0].y * scale, size / 2, 0, Math.PI * 2);
        ctx.fill();
        continue;
      }
      ctx.lineWidth = size;
      ctx.beginPath();
      ctx.moveTo(pts[0].x * scale, pts[0].y * scale);
      for (let i = 1; i < pts.length; i += 1) ctx.lineTo(pts[i].x * scale, pts[i].y * scale);
      ctx.stroke();
    }
    const blob = await new Promise<Blob | null>((res) => mask.toBlob(res, 'image/png'));
    if (!blob) return;
    onSave(new File([blob], 'mask.png', { type: 'image/png' }));
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-xl p-4 max-w-full max-h-full overflow-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-slate-800">Draw mask</h3>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700"
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <p className="text-xs text-slate-500 mb-3">
          Paint over the region to include in the mask (shown in red). The saved mask is
          white on black at the image&apos;s full resolution.
        </p>

        {loadError ? (
          <div className="rounded-lg border border-danger/30 bg-danger/5 text-danger px-3 py-6 text-sm text-center">
            Could not load the source image for drawing.
          </div>
        ) : !display ? (
          <div className="px-3 py-10 text-sm text-slate-400 text-center">Loading image…</div>
        ) : (
          <div
            className="relative mx-auto rounded-lg overflow-hidden border border-slate-200 bg-slate-900"
            style={{ width: display.w, height: display.h }}
          >
            <img
              src={imageSrc}
              alt="Source"
              className="absolute inset-0 w-full h-full object-contain select-none pointer-events-none"
              draggable={false}
            />
            <canvas
              ref={(el) => {
                overlayRef.current = el;
                if (el) redraw();
              }}
              width={display.w}
              height={display.h}
              onPointerDown={onDown}
              onPointerMove={onMove}
              onPointerUp={onUp}
              onPointerCancel={onUp}
              className="absolute inset-0 touch-none cursor-crosshair"
            />
          </div>
        )}

        <div className="mt-4 flex items-center justify-between gap-4 flex-wrap">
          <label className="flex items-center gap-2 text-xs font-medium text-slate-500">
            Brush
            <input
              type="range"
              min={4}
              max={80}
              value={brush}
              onChange={(e) => setBrush(Number(e.target.value))}
            />
            <span className="w-8 tabular-nums text-slate-600">{brush}px</span>
          </label>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={undo}
              disabled={!hasStrokes}
              className="px-3 py-1.5 rounded-lg border border-slate-200 text-xs font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Undo
            </button>
            <button
              type="button"
              onClick={clearAll}
              disabled={!hasStrokes}
              className="px-3 py-1.5 rounded-lg border border-slate-200 text-xs font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Clear
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-1.5 rounded-lg border border-slate-200 text-xs font-medium text-slate-600 hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={save}
              disabled={!hasStrokes || loadError}
              className="px-4 py-1.5 rounded-lg bg-indigo-500 text-white text-xs font-medium hover:bg-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Use mask
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
